import { useEffect, useRef, useState } from "react";
import type * as PdfjsTypes from "pdfjs-dist";
import { Icon } from "../../shell/Icon";

// pdfjs evaluiert DOMMatrix beim Modul-Laden — das gibt's in jsdom nicht.
// Dynamic-Import hält die DOMMatrix-Anforderung aus dem Test-Setup raus
// und macht das Modal Test-safe. Die Worker-URL kommt ebenfalls per
// Vite-?url-Import lazy.
let pdfjsModule: typeof PdfjsTypes | null = null;
let pdfjsInit: Promise<typeof PdfjsTypes> | null = null;
async function loadPdfjs(): Promise<typeof PdfjsTypes> {
  if (pdfjsModule) return pdfjsModule;
  if (!pdfjsInit) {
    pdfjsInit = (async () => {
      const mod = await import("pdfjs-dist");
      const workerUrl = (
        await import("pdfjs-dist/build/pdf.worker.min.mjs?url")
      ).default;
      mod.GlobalWorkerOptions.workerSrc = workerUrl;
      pdfjsModule = mod;
      return mod;
    })();
  }
  return pdfjsInit;
}

/**
 * Inline-PDF-Vorschau im Dark-Theme. Lädt das PDF via Backend-File-Stream
 * (`/api/.../eingang/<relPath>`) und rendert Seite für Seite auf Canvas.
 * Prev/Next-Buttons + Page-Indikator unten.
 */
export function PdfPreviewModal({
  url,
  fileName,
  onClose,
}: {
  url: string;
  fileName: string;
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<PdfjsTypes.PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setPageNum((p) => Math.min(p + 1, pageCount));
      if (e.key === "ArrowLeft") setPageNum((p) => Math.max(p - 1, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, pageCount]);

  // PDF laden — pdfjs lazy importieren (siehe oben).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadPdfjs()
      .then((mod) => mod.getDocument(url).promise)
      .then((doc) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        setPdf(doc);
        setPageCount(doc.numPages);
        setPageNum(1);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Aktuelle Seite rendern
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    pdf.getPage(pageNum).then(async (page) => {
      if (cancelled || !canvasRef.current) return;
      // Scale so dass die Seite in den Modal-Body passt (max 720 breit).
      const viewport0 = page.getViewport({ scale: 1 });
      const targetWidth = 720;
      const scale = Math.min(2, targetWidth / viewport0.width);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    });
    return () => {
      cancelled = true;
    };
  }, [pdf, pageNum]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pdf) pdf.destroy();
    };
  }, [pdf]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 860 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">/ QUELLE / PDF-VORSCHAU</div>
            <div className="modal-title" style={{ fontSize: 18 }}>
              {fileName}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div
          className="modal-body"
          style={{
            display: "grid",
            placeItems: "center",
            padding: 18,
            background: "var(--bg-void)",
            maxHeight: "75vh",
            overflow: "auto",
          }}
        >
          {loading && (
            <div className="dim" style={{ padding: 40 }}>
              <span className="spinner" style={{ borderColor: "var(--acc)", borderRightColor: "transparent" }} /> PDF wird geladen …
            </div>
          )}
          {error && (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>PDF nicht ladbar:</strong>{" "}
              <span className="mono">{error}</span>
            </div>
          )}
          {!loading && !error && (
            <canvas
              ref={canvasRef}
              style={{
                background: "white",
                boxShadow: "0 20px 60px -10px oklch(0 0 0 / 0.6)",
                maxWidth: "100%",
              }}
            />
          )}
        </div>

        <div className="modal-foot">
          <button
            type="button"
            className="btn ghost"
            disabled={pageNum <= 1}
            onClick={() => setPageNum((p) => Math.max(p - 1, 1))}
          >
            ← Vorherige
          </button>
          <span
            className="mono"
            style={{ color: "var(--fg-dim)", fontSize: 12, padding: "0 12px" }}
          >
            Seite {pageNum} / {pageCount || "…"}
          </span>
          <button
            type="button"
            className="btn ghost"
            disabled={pageNum >= pageCount}
            onClick={() => setPageNum((p) => Math.min(p + 1, pageCount))}
          >
            Nächste →
          </button>
          <div style={{ flex: 1 }} />
          <a
            href={url}
            download={fileName}
            className="btn"
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="download" size={14} /> Original
          </a>
        </div>
      </div>
    </div>
  );
}
